import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject, combineLatest, filter, map, take, takeUntil } from 'rxjs';
import { selectHasBundled, selectHeaderStateServiceError, selectInstructions, selectIsDemoUser, selectIsEmployee, selectIsIip, selectIsPensionTrustAccount, selectIsPtrs, selectMoveMoneyDataAccess } from '../../../account-info-header/reducers';
import { selectHasAccessInitiateMoneyMovement, selectHasRequestWire, selectHasUpdateMoneyLink, selectHasUpdateMoveMoney } from '../../../authorize/reducers';
import { CheckActions, InternalTransferActions, PtrsActions, ReviewPageActions, TransactionsTableActions } from '../../../create-request/actions';
import { TransactionFormServiceError } from '../../../create-request/models/form/TransactionForm';
import { selectAchTxnCode, selectCurrentTransaction, selectInternalTransferInfo, selectInternalTransfersTxnCode, selectIsPendingForLoa, selectPendReasons, selectStagedTransactionType, selectTransactionsTableCurrentTemplateId } from '../../../create-request/reducers';
import { SharedActions } from '../../../shared/actions';
import { selectPdf, selectViewInContext, selectViewPage, selectViewPageServiceError } from '../../../shared/reducers';
import { FeatureFlagService } from '../../../shared/services/feature-flag-service';
import { ComponentState } from '../../../shared/types/ComponentState';
import { selectedRowData } from '../../../shared/types/TableComponentState';
import { Constants, transactionDirection, transactionTableTabs, transactionTypes, viewPageTitles, PageNameTypes, pageInContext } from '../../../shared/utilities/Constants';
import { getArrayBufferFromBase64, openPdfInNewWindow } from '../../../shared/utilities/pdf';
import { StandingAuthAchActions, StandingAuthCheckActions } from '../../../standing-authorizations/actions';
import { ViewPageActions } from '../../actions';
import { ViewDetails } from '../../models/ViewDetails';
import { selectCashieringRequestId, selectCashieringRequestSecondId, selectInquiryCallComplete, selectIsCancelable, selectIsEAuth, selectIsEditable, selectIsIncoming, selectViewDetails, selectViewFrequency, selectViewPageTransactionType } from '../../reducers';
import { EAuthStatusMessageFields } from '../../../create-request/models/internal-transfer/InternalTransferInquiryResponse';
import { positionPage } from '../../../shared/utilities/PrintPageUtils';

@Component({
  standalone: false,
  selector: 'mm-view-page',
  templateUrl: './view-page.component.html',
  styleUrls: ['./view-page.component.scss']
})
export class ViewPageComponent implements OnInit {
  pageTitle: string;
  viewPageComponentState$: Observable<ComponentState>;
  viewPageServiceError$: Observable<TransactionFormServiceError>;
  hasViewPageServiceError: boolean = false;
  hasHeaderStateServiceError: boolean;
  destroy$: Subject<boolean> = new Subject<boolean>();
  viewDetails$: Observable<ViewDetails>;
  transactionType$: Observable<string>;
  transactionType: string;
  isPendingForLoa$: Observable<boolean>;
  pdf: string;
  pendReasons$: Observable<string[]>;
  isPendingForLoa: boolean;
  cashieringRequestId: string;
  cashieringRequestSecondId: string;
  isStandingAuth: boolean;
  cancelTransactionButtonVisible: boolean;
  cancelButtonClicked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  cancelButtonClickedAsObservable$ = this.cancelButtonClicked$.asObservable();
  private deleteButtonClicked$ = new BehaviorSubject<boolean>(false);
  deleteButtonClickedAsObservable$ = this.deleteButtonClicked$.asObservable();
  private currentSelectedTransaction$ = new BehaviorSubject<string | null>(null);
  currentSelectedTransactionAsObservable$ = this.currentSelectedTransaction$.asObservable();
  editTransactionButtonVisible: boolean;
  editButtonClicked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  editButtonClickedAsObservable$ = this.editButtonClicked$.asObservable();
  hasUpdateMoveMoneyAccess: boolean;
  hasUpdateMoneyLinkAccess: boolean;
  hasRequestWireAccess: boolean;
  isFromView: boolean = true;
  achTxnCode: string;
  itTxnCode: string;
  isEmployee: boolean;
  isIip: boolean;
  isDemoUser: boolean;
  isBundledTransaction: boolean;
  isTemplateView: boolean = false;
  templateInContext: string;
  private deleteTemplateClicked$ = new BehaviorSubject<boolean>(false);
  deleteTemplateLinkClickedAsObservable$ = this.deleteTemplateClicked$.asObservable();
  private currentSelectedTemplate$ = new BehaviorSubject<string | null>(null);
  currentSelectedTemplateAsObservable$ = this.currentSelectedTemplate$.asObservable();
  isEauth: boolean;
  custAcctAuthorizations: EAuthStatusMessageFields[] = [];
  wireRetryWarningBannerText: string = Constants.wireViewPageRetryWarning;
  hasMmInitiateAccess:boolean = false;
  schwabLogo: string;
  pageInContext: PageNameTypes;
  useOriginalLayout: boolean = true;
  isCheckTransaction: boolean;
  isAchTransaction: boolean;
  useCondensedLayoutForIt: boolean = false;

  constructor(private store: Store, private featureFlagService: FeatureFlagService, private toastr: ToastrService) { }

  ngOnInit(): void {
    this.scrollToTop();

    this.store.select(selectHasUpdateMoveMoney).pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.hasUpdateMoveMoneyAccess = val;
    });
    this.store.select(selectHasUpdateMoneyLink).pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.hasUpdateMoneyLinkAccess = val;
    });
    this.store.select(selectHasRequestWire).pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.hasRequestWireAccess = val;
    });
    this.store.select(selectAchTxnCode).pipe(takeUntil(this.destroy$)).subscribe(val => {
      val ? this.achTxnCode = val : this.achTxnCode = '';
    });
    this.store.select(selectViewInContext).pipe(takeUntil(this.destroy$)).subscribe(page => {
      this.pageInContext = page;
    })

    this.store.select(selectTransactionsTableCurrentTemplateId).pipe(takeUntil(this.destroy$)).subscribe(id => this.templateInContext = id ?? '');

    this.store.select(selectInternalTransfersTxnCode).pipe(takeUntil(this.destroy$)).subscribe(val => this.itTxnCode = val);

    this.viewPageComponentState$ = this.store.select(selectViewPage);

    this.viewPageServiceError$ = this.store.select(selectViewPageServiceError);
    this.viewPageServiceError$.pipe(takeUntil(this.destroy$)).subscribe(val => {
      if (val.hasError && val.message) {
        this.hasViewPageServiceError = val.hasError;
      }
    })

    this.store.select(selectHeaderStateServiceError).pipe(takeUntil(this.destroy$)).subscribe(hasError => {
      this.hasHeaderStateServiceError = hasError;
    })

    this.viewDetails$ = this.store.select(selectViewDetails);
    this.viewDetails$.pipe(
      takeUntil(this.destroy$),
      filter((viewDetails) => !!(viewDetails.type.trim()))
    ).subscribe((viewDetails) => {
      let isAchProfile = viewDetails.transactionType === 'ach' && viewDetails.status?.code === 'ACTV';
      this.isStandingAuth = !(viewDetails.type === 'transaction');
      if (viewDetails.type === 'template') {
        this.isTemplateView = true;
        this.pageTitle = viewPageTitles.template;
      }
      else {
        this.pageTitle =
        this.isStandingAuth || isAchProfile
          ? viewPageTitles.standingAuth
          : viewPageTitles.transaction;
        this.isBundledTransaction = viewDetails.isBundled
      }

      this.cashieringRequestId = viewDetails.cashieringRequestId;
      this.cashieringRequestSecondId = viewDetails.cashieringRequestSecondId;
      this.custAcctAuthorizations = viewDetails?.eAuthStatus?.customerList || [];
    });
    this.pendReasons$ = this.store.select(selectPendReasons);
    this.transactionType$ = this.store.select(selectStagedTransactionType);
    this.isPendingForLoa$ = this.store.select(selectIsPendingForLoa);
    this.store.select(selectViewDetails).subscribe(viewDetails => this.isEauth = viewDetails?.eAuthStatus?.isEAuth ?? false)

    this.isPendingForLoa$.pipe(takeUntil(this.destroy$)).subscribe(_isPendingForLoa => this.isPendingForLoa = _isPendingForLoa);

    this.transactionType$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(transactionType => this.transactionType = transactionType);

    this.store.select(selectPdf).pipe(
      takeUntil(this.destroy$)
    ).subscribe(data => {
      if (data) {
        this.pdf = data;
        let buffer = getArrayBufferFromBase64(data);
        let pdf = new Blob([buffer], { type: 'application/pdf' });
        openPdfInNewWindow(pdf, false);
      }
    });

    combineLatest([
      this.store.select(selectIsEmployee),
      this.store.select(selectIsIip),
      this.store.select(selectIsDemoUser),
      this.store.select(selectMoveMoneyDataAccess),
      this.store.select(selectIsCancelable),
      this.store.select(selectIsEditable),
      this.store.select(selectInquiryCallComplete),
      this.store.select(selectHasBundled),
      this.store.select(selectViewPageTransactionType),
      this.store.select(selectIsPensionTrustAccount)
    ]).pipe(
      takeUntil(this.destroy$),
      filter(([_isEmployee, _isIip, _isDemoUser, _hasMmDataAccess, _isCancelable, _isEditable, inquiryCallComplete, _bundledOffer, _transactionType, _isPensionTrustAccount]) => inquiryCallComplete)
    ).subscribe(([isEmployee, isIip, isDemoUser, hasMmDataAccess, isCancelable, isEditable, _inquiryCallComplete, bundledOffer, transactionType, isPensionTrustAccount]) => {
      const hasCancelAccess = this.getHasCancelAccess();
      this.cancelTransactionButtonVisible =
        isCancelable && hasMmDataAccess && !isEmployee && !isIip && !isDemoUser && hasCancelAccess ? true : false;
      const hasEditAccess = this.getHasEditAccess();
      const isNotBundledOrIsEditable = this.isBundledTransaction ? bundledOffer : true;
      this.editTransactionButtonVisible =
        (hasMmDataAccess || isEmployee || isDemoUser) && isEditable && hasEditAccess && !isIip && isNotBundledOrIsEditable  && !(transactionType === 'ach' && isPensionTrustAccount) ? true : false;
    });

    this.store
      .select(selectIsEmployee)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isEmployee) => (this.isEmployee = isEmployee));

    this.store.select(selectIsDemoUser).subscribe(value => {
      this.isDemoUser = value;
    });

    this.store.select(selectIsIip).subscribe(value => {
      this.isIip = value;
    });

    this.store.select(selectHasAccessInitiateMoneyMovement)
    .pipe(takeUntil(this.destroy$))
    .subscribe(response => {
      this.hasMmInitiateAccess =response;
    });

    combineLatest([
      this.store.select(selectViewPageTransactionType),
      this.store.select(selectIsIncoming),
      this.viewDetails$,      
      this.store.select(selectInternalTransferInfo)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([ transactionType, isIncoming, viewDetails, itInfo]) => {
      this.isAchTransaction = transactionType === transactionTypes.ach;
      this.isCheckTransaction = transactionType === transactionTypes.check;
      let isItTransaction = transactionType === transactionTypes.internalTransfer;
      let isStandingAuth = viewDetails.type !== 'transaction';
      let isPositionsTransaction = itInfo.positionsInfo.hasSelected !== undefined ? itInfo.positionsInfo.hasSelected : false
      if (this.isAchTransaction && !isStandingAuth) {
        this.useCondensedLayoutForIt = false;
        this.useOriginalLayout = false;
      } else if (this.isCheckTransaction && !isIncoming && !isStandingAuth) {
        this.useCondensedLayoutForIt = false;
        this.useOriginalLayout = false;
      } else if (isItTransaction && !isPositionsTransaction && !isStandingAuth) {
        this.useCondensedLayoutForIt = true;
        this.useOriginalLayout = !this.useCondensedLayoutForIt;
      } else {
        this.useOriginalLayout = true;
        this.useCondensedLayoutForIt = false;
      }
    });
  }

  scrollToTop(): void {
    const elem = document.getElementById('view-page-header');
    setTimeout(function () {
      elem?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  getHasCancelAccess(): boolean {
    if (this.transactionType === transactionTypes.ach) {
      return this.hasUpdateMoneyLinkAccess;
    }
    if (this.transactionType === transactionTypes.wire) {
      return this.hasRequestWireAccess;
    }
    return this.hasUpdateMoveMoneyAccess;
  }

  getHasEditAccess(): boolean {
    switch (this.transactionType){
      case transactionTypes.ach:
        return this.hasUpdateMoneyLinkAccess;
      case transactionTypes.check:
        return this.hasUpdateMoveMoneyAccess;
      case transactionTypes.internalTransfer:
        return this.hasUpdateMoveMoneyAccess;
      default:
        return false;
    }
  }

  onBackClicked(): void {
    if (this.pageInContext === pageInContext.viewMsv) {
      this.store.dispatch(ViewPageActions.backButtonClickedFromMsv())
    }
    else {
      this.store.dispatch(ViewPageActions.backButtonClicked());
    }
  }

  onStartTransactionClicked(): void {
    this.store.dispatch(TransactionsTableActions.currentTabUpdated({currentTab: transactionTableTabs.all}));
    this.store.dispatch(ViewPageActions.startTransactionButtonClicked({draftId: this.templateInContext}))
  }

  onViewAuthFormClicked(): void {
    if (this.isStandingAuth) {
      this.store.dispatch(ViewPageActions.generateAuthorizationFormStandingAuth());
    } else {
      this.store.dispatch(ReviewPageActions.generateAuthorizationForm({ source: 'view' }));
    }
  }

  onCancelTransactionButtonClicked(): void {
    this.cancelButtonClicked$.next(true);
  }

  onDeleteTransactionButtonClicked(): void {
    if (this.isDemoUser) {
      this.toastr.error(Constants.toastrMessageDemoUser);
    }
    else if (this.isEmployee) {
      this.toastr.error(Constants.toastrMessageEmployee);
    }
    else {
      this.currentSelectedTransaction$.next(this.transactionType[0] + '-' + this.cashieringRequestId + '-' + this.cashieringRequestSecondId)
      this.deleteButtonClicked$.next(true);
    }
  }

  onDeleteTemplateButtonClicked(): void {
    this.currentSelectedTemplate$.next(this.templateInContext)
    this.deleteTemplateClicked$.next(true);
  }

  onDeleteClicked(): void {
    if (this.isTemplateView) {
      this.onDeleteTemplateButtonClicked();
    }
    else {
      this.onDeleteTransactionButtonClicked();
    }
  }

  onEditTransactionButtonClicked(): void {
    if (this.transactionType === transactionTypes.ach || ( this.isStandingAuth && this.transactionType === transactionTypes.check)) {
      this.callInquiry();
    }
    else {
      this.store.select(selectIsEAuth).pipe(takeUntil(this.destroy$))
        .subscribe(isEauth => {
          if (isEauth === true) {
            this.store.dispatch(SharedActions.showEditingRestrictedModal());
          } else if (isEauth === false) { //do not execute action when isEauth is undefined
            this.store.dispatch(ViewPageActions.editButtonClicked());
            this.isFromView = false;
            this.callInquiry(); //we need to call Inquiry a second time with isFromView: false
          }
        })
    }
  }

  callInquiry() {
    combineLatest([
      this.store.select(selectCashieringRequestId),
      this.store.select(selectCashieringRequestSecondId),
      this.store.select(selectIsIncoming),
      this.store.select(selectIsPtrs),
      this.store.select(selectViewFrequency)
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([cashieringReqId, cashieringReqSecondId, isIncoming, isPtrs, frequency]) => {
      if (this.transactionType === transactionTypes.ach) {
        let rowData: selectedRowData = {
          transactionType: transactionTypes.ach,
          transferDirection: isIncoming ? 'incoming' : 'outgoing',
          standingAuthId: `a-${cashieringReqId}-${cashieringReqSecondId}`,
          frequency: frequency === 'On-Request' ? 'onetime' : 'recurring'
        };
        this.store.dispatch(
          ViewPageActions.editProfileClicked({ rowData })
        );
        let achPayload: any = {
          cashieringRqstId: cashieringReqId,
          cashieringRqstSecondId: cashieringReqSecondId,
          txnCode: this.achTxnCode
        }
        this.store.dispatch(StandingAuthAchActions.callAchInquiry({
          payload: achPayload,
          viewType: 'standing-auth',
          transactionType: transactionTypes.ach
        }));
      }
      else if (this.transactionType === transactionTypes.check) {
        if (!this.isStandingAuth) {
          let checkPayload: any = {
            cashieringRqstId: cashieringReqId,
            cashieringRqstSecondId: cashieringReqSecondId,
            fundsDirection: isIncoming ? transactionDirection.in : transactionDirection.out
          }
          this.store.dispatch(CheckActions.callInquiry({
            payload: checkPayload,
            viewType: this.isFromView ? 'transaction' : '',
            transactionType: transactionTypes.check
          }));
        } else if (this.isStandingAuth) {
          let rowData: selectedRowData = {
            transactionType: transactionTypes.check,
            standingAuthId: `a-${cashieringReqId}-${cashieringReqSecondId}`,
            frequency: frequency === 'On-Request' ? 'onetime' : 'recurring'
          };
          this.store.dispatch(
            ViewPageActions.editProfileClicked({ rowData })
          );
          this.store.select(selectInstructions).pipe(
            take(1),
            map((instructions) =>
              instructions.find((instructionOrProfile) => {
                return (
                  instructionOrProfile.cashieringRqstId ===
                  cashieringReqId
                );
              })
            )
          ).subscribe((res) => {
            if (res) {
              this.store.dispatch(
                StandingAuthCheckActions.callCheckInquiry({
                  payload: res,
                  viewType: 'standing-auth',
                  transactionType: transactionTypes.check,
                })
              );
            }
          });
        }
      } else if (this.transactionType === transactionTypes.internalTransfer) {
        let internalTrasfersPayload: any = {
          cashieringRqstId: cashieringReqId,
          cashieringRqstSecondId: cashieringReqSecondId,
          txnCode: this.itTxnCode
        }
        this.store.dispatch(InternalTransferActions.callInquiry({
          payload: internalTrasfersPayload,
          viewType: 'transaction',
          transactionType: transactionTypes.internalTransfer
        }));
      }
    });
  }

  downloadPdf() {
    setTimeout(() => {
      let temp = document.title;
      document.title = 'Move Money - Schwab Advisor Center';
      try {
        positionPage('print-view-page');
        if (!document.execCommand('print', false, '')) {
          window.print();
        }
      } catch {
        positionPage('print-view-page');
        window.print();
      }
      document.title = temp;
    }, 10);
  }

  get showViewLoaButton() {
    return !this.isEauth && this.isPendingForLoa && (this.transactionType === transactionTypes.check || this.transactionType === transactionTypes.internalTransfer ? !this.isBundledTransaction : true);
  }

  get showWireEAuthStatusUnavailableWarning() {
    return ((this.custAcctAuthorizations === undefined || this.custAcctAuthorizations?.length === 0) && this.isEauth && this.transactionType === transactionTypes.wire && this.pageInContext !== pageInContext.viewMsv)
  }

  get isInRFirm(): boolean {
    return this.hasMmInitiateAccess;
  }

  get showClientAuthorizationDetails(): boolean {
    if(this.isStandingAuth || (!this.useOriginalLayout && !this.isEauth)) {
      return false;
    } else {
      return !this.useOriginalLayout && this.isEauth && this.custAcctAuthorizations !== undefined && this.custAcctAuthorizations.length > 0;
    }
  }
}
